// src/components/Dashboard/QuickAddTransaction.js
// Minimal, keyboard-friendly dialog for fast transaction entry.
// NO split transactions, NO receipts, NO recurring — intentionally minimal.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Grid,
  Snackbar,
  Alert,
  CircularProgress,
  Typography,
  InputAdornment,
  IconButton,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import api from '../../services/api';
import { useBudget } from '../../contexts/BudgetContext';
import { useAccount } from '../../contexts/AccountContext';

// Format today's date as YYYY-MM-DD for <input type="date">
const todayISO = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const DEFAULT_FORM = {
  amount: '',
  description: '',
  categoryId: '',
  accountId: '',
  date: todayISO(),
  type: 'expense',
};

/**
 * QuickAddTransaction
 *
 * Props:
 *   open        {boolean}  — dialog open/close
 *   onClose     {function} — called when dialog should close
 *   initialType {string}   — 'expense' | 'income' | 'transfer'  (pre-selects radio)
 *   onSuccess   {function} — optional callback after successful save
 */
const QuickAddTransaction = ({ open, onClose, initialType = 'expense', onSuccess }) => {
  const { categories, syncWithServer } = useBudget();
  const { accounts, defaultAccount } = useAccount();

  const [form, setForm] = useState({ ...DEFAULT_FORM, type: initialType });
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const amountRef = useRef(null);

  // Reset form & update type whenever dialog opens or initialType changes
  useEffect(() => {
    if (open) {
      const initAccountId =
        initialType === 'income' ? (defaultAccount?._id || '') : '';
      setForm({ ...DEFAULT_FORM, date: todayISO(), type: initialType, accountId: initAccountId });
      // Autofocus amount after dialog transition settles
      setTimeout(() => {
        if (amountRef.current) amountRef.current.focus();
      }, 150);
    }
  }, [open, initialType, defaultAccount]);

  // Keyboard handler — Enter submits, Escape closes
  const handleKeyDown = useCallback(
    (e) => {
      if (!open) return;
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        // Ctrl+Enter / Cmd+Enter submits from anywhere in the form
        handleSubmit(e);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open, form]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (submitting) return;

    // Basic validation
    const amount = parseFloat(form.amount);
    if (!form.amount || isNaN(amount) || amount <= 0) {
      setSnackbar({ open: true, message: 'Please enter a valid amount', severity: 'error' });
      return;
    }

    // Resolve category name from ID
    const selectedCategory = categories.find((c) => c.id === form.categoryId);

    const payload = {
      amount,
      description: form.description || (selectedCategory ? selectedCategory.name : 'Quick entry'),
      category: selectedCategory ? selectedCategory.name : '',
      categoryId: form.categoryId || undefined,
      accountId: form.accountId || undefined,
      date: form.date || todayISO(),
      type: form.type,
      paymentMethod: 'other',
    };

    try {
      setSubmitting(true);
      await api.post('/transactions', payload);

      // Refresh context so dashboard reflects the new transaction
      if (typeof syncWithServer === 'function') {
        syncWithServer().catch(() => {}); // non-blocking
      }
      // Signal to AccountContext to refresh balances
      window.dispatchEvent(new Event('soldikeeper:accounts-refresh'));

      setSnackbar({ open: true, message: 'Transaction added ✓', severity: 'success' });

      if (typeof onSuccess === 'function') onSuccess(payload);
      onClose();
    } catch (err) {
      console.error('QuickAddTransaction error:', err);
      const msg =
        err?.response?.data?.message || err?.message || 'Failed to save transaction';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  // Filter categories by selected type (for income/expense)
  const filteredCategories =
    form.type === 'transfer'
      ? categories
      : categories.filter((c) => !c.type || c.type === form.type || c.type === 'both');

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            backgroundImage: 'none',
          },
        }}
        // Keep scroll inside dialog
        scroll="paper"
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 1,
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Quick Add Transaction
          </Typography>
          <IconButton size="small" onClick={onClose} aria-label="Close dialog">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ pt: 2 }}>
          <Box component="form" onSubmit={handleSubmit} noValidate>
            {/* Type selector */}
            <RadioGroup
              row
              value={form.type}
              onChange={handleChange('type')}
              sx={{ mb: 2, justifyContent: 'center' }}
            >
              <FormControlLabel
                value="expense"
                control={<Radio color="error" size="small" />}
                label="Expense"
              />
              <FormControlLabel
                value="income"
                control={<Radio color="success" size="small" />}
                label="Income"
              />
              <FormControlLabel
                value="transfer"
                control={<Radio color="info" size="small" />}
                label="Transfer"
              />
            </RadioGroup>

            <Grid container spacing={2}>
              {/* Amount */}
              <Grid item xs={12} sm={6}>
                <TextField
                  inputRef={amountRef}
                  label="Amount"
                  type="number"
                  value={form.amount}
                  onChange={handleChange('amount')}
                  fullWidth
                  required
                  size="small"
                  inputProps={{ min: 0, step: '0.01' }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AttachMoneyIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSubmit(e);
                  }}
                />
              </Grid>

              {/* Date */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Date"
                  type="date"
                  value={form.date}
                  onChange={handleChange('date')}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  value={form.description}
                  onChange={handleChange('description')}
                  fullWidth
                  size="small"
                  placeholder="Optional note…"
                />
              </Grid>

              {/* Category */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="qat-category-label">Category</InputLabel>
                  <Select
                    labelId="qat-category-label"
                    value={form.categoryId}
                    onChange={handleChange('categoryId')}
                    label="Category"
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {filteredCategories.map((cat) => (
                      <MenuItem key={cat.id} value={cat.id}>
                        {cat.icon ? `${cat.icon} ` : ''}
                        {cat.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Account */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="qat-account-label">Account</InputLabel>
                  <Select
                    labelId="qat-account-label"
                    value={form.accountId}
                    onChange={handleChange('accountId')}
                    label="Account"
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {accounts.map((acc) => (
                      <MenuItem key={acc._id || acc.id} value={acc._id || acc.id}>
                        {acc.name}
                        {acc.balance != null ? ` · ${acc.currency || '$'}${Number(acc.balance).toFixed(2)}` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            Ctrl+Enter to submit
          </Typography>
          <Button onClick={onClose} disabled={submitting} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {submitting ? 'Saving…' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar feedback — rendered outside the dialog so it persists after close */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default QuickAddTransaction;
